import { EVENT_TYPES } from '$events';
import { Sentry } from '$lib/services/sentry';
import { Analytics, type EventData } from './client';

/** Fixed tuning for `client.page_scroll` depth and section tracking. */
export const PAGE_SCROLL_CONFIG = Object.freeze({
	/** Depth is reported at every multiple of this percent: 10, 20, 30 … */
	depthStepPercent: 10,
	/** A mark is never thinner than this. Short pages widen the step instead. */
	minMarkPixels: 100,
	/** Attribute that marks a page section whose visibility is reported on scroll. */
	sectionAttribute: 'data-track-section',
	/** Share of a section, or of the viewport, that must be on screen to count as seen. */
	sectionVisibilityRatio: 0.5,
	/** Wait until a scroll gesture pauses before sending the mark it settled on. */
	settleMs: 150,
});

/** Steps a page may use, widest last. The first one that is at least `minMarkPixels` wins. */
const PAGE_SCROLL_DEPTH_STEPS = Object.freeze([10, 25, 50, 100]);

/** What the tracker reads of a page section. */
type TrackedSection = Pick<Element, 'getAttribute' | 'getBoundingClientRect'>;

/** The parts of `window` the tracker reads, so a test can hand in a fake. */
export interface PageScrollWindow {
	clearTimeout(handle: number): void;
	document: {
		documentElement: Pick<HTMLElement, 'scrollHeight'>;
		querySelectorAll(selector: string): Iterable<TrackedSection>;
	};
	innerHeight: number;
	scrollY: number;
	setTimeout(callback: () => void, ms: number): number;
}

type PageScrollLogger = (
	type: typeof EVENT_TYPES.CLIENT_PAGE_SCROLL,
	data: EventData<'client.page_scroll'>
) => unknown;

/**
 * Per-page `client.page_scroll` state, cleared together by `reset`.
 * `lastMark` is the mark the visitor is currently on (0 above the first mark),
 * not the deepest one, and null until the first scroll on this page.
 * `lastSentMark` is the mark last logged after a gesture settled, so a
 * return to that mark logs again only after the visitor has left it.
 * `pendingSections` is the viewport sample from the latest mark change.
 * `sectionsViewed` holds every tracked section id seen at a depth mark on
 * this page. `settleTimer` waits out the current gesture.
 */
interface PageScrollState {
	lastMark: number | null;
	lastSentMark: number | null;
	pendingSections: string[];
	sectionsViewed: Set<string>;
	settleTimer: number | null;
}

function createState(): PageScrollState {
	return {
		lastMark: null,
		lastSentMark: null,
		pendingSections: [],
		sectionsViewed: new Set(),
		settleTimer: null,
	};
}

/**
 * Percent step for this page. Long pages stay on `depthStepPercent`. A shorter
 * page widens to 25, 50, or 100 so one mark is at least `minMarkPixels` tall.
 * @param scrollableHeight Pixels of document below the viewport.
 * @returns The step, or `undefined` when the leftover scroll is shorter than
 * `minMarkPixels` and there is nothing to report.
 */
function resolveScrollDepthStep(scrollableHeight: number): number | undefined {
	const { depthStepPercent, minMarkPixels } = PAGE_SCROLL_CONFIG;
	if (scrollableHeight < minMarkPixels) return undefined;

	const percentForMinPixels = (minMarkPixels / scrollableHeight) * 100;
	const minimumStep = Math.max(depthStepPercent, percentForMinPixels);
	return PAGE_SCROLL_DEPTH_STEPS.find((step) => step >= minimumStep);
}

/**
 * Current scroll depth snapped down to this page's depth step.
 * @returns The current mark (0, 10, 20 …, not capped at 100), or `undefined`
 * when the document is not scrollable enough to report.
 */
function resolveScrollDepthMark(win: PageScrollWindow): number | undefined {
	const scrollableHeight =
		(win.document.documentElement.scrollHeight || 0) - (win.innerHeight || 0);
	if (scrollableHeight <= 0) return undefined;

	const depthStepPercent = resolveScrollDepthStep(scrollableHeight);
	if (depthStepPercent === undefined) return undefined;

	const percentScrolled = ((win.scrollY || 0) / scrollableHeight) * 100;
	const nonNegativePercent = Math.max(0, percentScrolled);
	return Math.floor(nonNegativePercent / depthStepPercent) * depthStepPercent;
}

/**
 * Ids of the `data-track-section` elements currently on screen.
 *
 * A section counts as on screen when at least
 * `PAGE_SCROLL_CONFIG.sectionVisibilityRatio` of its own height is visible, or
 * when it fills at least that share of the viewport. The second rule is what
 * lets a section taller than the viewport qualify. Zero-height (hidden)
 * sections never qualify.
 * @returns Section ids in document order; empty when none are tagged.
 */
function resolveSectionsInViewport(win: PageScrollWindow): string[] {
	const { sectionAttribute, sectionVisibilityRatio } = PAGE_SCROLL_CONFIG;
	const viewportHeight = win.innerHeight || 0;
	const sectionIds: string[] = [];

	for (const section of win.document.querySelectorAll(`[${sectionAttribute}]`)) {
		const rect = section.getBoundingClientRect();
		if (rect.height <= 0) continue;

		const visibleHeight = Math.max(
			0,
			Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
		);
		const fillsSection = visibleHeight / rect.height >= sectionVisibilityRatio;
		const fillsViewport = visibleHeight / viewportHeight >= sectionVisibilityRatio;
		if (fillsSection || fillsViewport) {
			sectionIds.push(section.getAttribute(sectionAttribute) ?? '');
		}
	}

	return sectionIds;
}

/**
 * Tracks scroll depth and section visibility for `client.page_scroll`.
 *
 * `handleScroll` records the scroll mark and sends the event once the gesture
 * has paused for `PAGE_SCROLL_CONFIG.settleMs`. A flick that crosses several
 * marks sends only the mark it settles on. Scrolling down or up both count.
 * A mark already reported is reported again on a later crossing, there is no
 * cap at 100, and the zero mark (near the top) is tracked but never sent.
 *
 * Each event also reports which `data-track-section` sections were on screen
 * at that mark (`sections_in_viewport`) and every section seen so far on this
 * page (`sections_viewed`). Sections are measured only when the mark changes,
 * never on every scroll event. Entering the zero mark measures them too, so
 * sections on the first screen count as viewed.
 * @param win The window to read. Defaults to the real one when first used, so
 * importing this module on the server (prerender) never touches `window`.
 * @param log Where events go. Defaults to `Analytics.log`, looked up per call
 * so a module that mocks the client is not touched at import time.
 */
export function createPageScrollTracker(
	win?: PageScrollWindow,
	log: PageScrollLogger = (type, data) => Analytics.log(type, data)
) {
	let state = createState();

	const currentWindow = (): PageScrollWindow => win ?? window;

	function clearSettleTimer(): void {
		if (state.settleTimer === null) return;
		currentWindow().clearTimeout(state.settleTimer);
		state.settleTimer = null;
	}

	/** Restarts the pause timer, so the mark is sent after the last scroll event of the gesture. */
	function scheduleFlush(): void {
		clearSettleTimer();
		state.settleTimer = currentWindow().setTimeout(() => {
			state.settleTimer = null;
			flush();
		}, PAGE_SCROLL_CONFIG.settleMs);
	}

	/**
	 * Sends the mark the gesture settled on. Settling at the top records the
	 * zero mark without sending it, so the next real mark can be sent again.
	 */
	function flush(): void {
		clearSettleTimer();
		const depthMark = state.lastMark;
		if (depthMark === null) return;
		if (depthMark === 0) {
			state.lastSentMark = 0;
			return;
		}
		if (depthMark === state.lastSentMark) return;

		state.lastSentMark = depthMark;
		void log(EVENT_TYPES.CLIENT_PAGE_SCROLL, {
			percentage_scrolled: depthMark,
			sections_in_viewport: state.pendingSections,
			sections_viewed: [...state.sectionsViewed],
		});
	}

	return {
		flush,

		/** Scroll listener. Safe to bind directly to `window`'s `scroll` event. */
		handleScroll(): void {
			try {
				const depthMark = resolveScrollDepthMark(currentWindow());
				if (depthMark === undefined) return;

				if (depthMark !== state.lastMark) {
					state.lastMark = depthMark;
					const sectionsInViewport = resolveSectionsInViewport(currentWindow());
					sectionsInViewport.forEach((id) => state.sectionsViewed.add(id));
					state.pendingSections = sectionsInViewport;
				}

				scheduleFlush();
			} catch (err) {
				Sentry.captureException(err, {
					extra: { action: 'Error: Scroll Tracking Handler' },
				});
			}
		},

		/**
		 * Clears per-page scroll depth and viewed sections so SPA navigation does
		 * not reuse the previous page's state. Call from `afterNavigate`, not
		 * `trackPageView`.
		 */
		reset(): void {
			clearSettleTimer();
			state = createState();
		},
	};
}

export type PageScrollTracker = ReturnType<typeof createPageScrollTracker>;

/** The tracker the layout drives, reading the real window. */
export const pageScrollTracker = createPageScrollTracker();
